<template>
  <div
    v-show="isOpen"
    ref="image-download"
    class="o-image-download-menu"
    :style="{ maxWidth: parentWidth }"
  >
    <div class="o-image-download-menu__download-links">
      <div class="o-image-download-menu__download-links-header">
        {{ imageDownloadHeader }}
      </div>
      <div class="o-image-download-menu__download-links-list">
        <ul ref="image-download-links" aria-label="Image Downloads">
          <li
            v-for="item in imageDownloadSizes"
            :key="item.description"
            :data-testid="'downloadDropdownOption-' + item.width"
            class="o-image-download-menu__link-item"
            :aria-label="item.ariaResolution"
          >
            <GuiLink
              :id="`download${item.width}`"
              class="o-image-download-menu__link-link"
              :aria-label="item.ariaDownload"
              :href="item.href"
              target="_self"
              with-icon="before"
              icon-name="Download"
            >
              {{ item.resolution }}
            </GuiLink>
          </li>
          <li
            v-if="showManifest"
            key="manifest"
            class="o-image-download-menu__link-item"
          >
            <GuiLink
              :href="miradorUrl"
              :aria-label="ariaIiif"
              rel="nofollow"
              class="o-image-download-menu__image"
            >
              <img
                src="@static/iiif.png"
                class="iiif-logo"
                :alt="iiifAltText"
              />
            </GuiLink>
            <GuiLink
              id="mirador"
              :aria-label="ariaIiif"
              :icon-name="'outbound'"
              :with-icon="'after'"
              target="_blank"
              class="o-image-download-menu__link-link"
              :href="miradorUrl"
              rel="nofollow"
            >
              {{ iiifViewerText }}
            </GuiLink>
          </li>
        </ul>
      </div>
    </div>
    <div ref="open-content" class="o-image-download-menu__item">
      <div class="o-image-download-menu__item-header">
        {{ openContentHeader }}
      </div>
      <div class="o-image-download-menu__item-text">
        {{ openContentText }}
        <GuiLink
          id="opencontentinfo"
          aria-label="ariaOpenContentInfoLink"
          class="o-image-download-menu__link-link"
          :href="openContentLink"
        >
          {{ openContentLinkText }} </GuiLink
        >. {{ openContentFaqText }}
        <GuiLink
          id="opencontentfaq"
          :aria-label="ariaOpenContentFaqLink"
          class="o-image-download-menu__link-link"
          :href="faqLink"
        >
          {{ openContentFaqLink }} </GuiLink
        >.
      </div>
    </div>
    <div v-if="false" ref="publication-use" class="o-image-download-menu__item">
      <div class="o-image-download-menu__item-header">
        {{ publicationUseHeader }}
      </div>
      <div class="o-image-download-menu__item-text">
        {{ publicationUsePre }}
        <GuiLink
          id="imagerequest"
          :aria-label="ariaOpenContentLink"
          class="o-image-download-menu__link-link"
          :to="{
            name: 'imageRequest',
            query: {
              type: 'openContent',
              id: [id],
              imageUrl: [imageFormServiceUrl]
            }
          }"
          rel="nofollow"
        >
          {{ publicationUseLink }}
        </GuiLink>
        {{ publicationUsePost }}
      </div>
    </div>
    <div class="o-image-download-menu__item">
      <div class="o-image-download-menu__item-text">
        <ObjectCitation
          ref="object-citation"
          :citation-fields="citationFields"
          :use-header-icon="true"
          :citation-header="citationHeader"
        />
      </div>
    </div>
  </div>
</template>

<script>
import * as configHelpers from "@/helpers/configHelper";
import ObjectCitation from "@/components/atoms/objectCitation/index.vue";
import {
  GuiLink,
  useLocalization,
  breakpointsMixin,
  useEventBus
} from "@thegetty-private/getty-ui";
import mobileMixin from "@/mixins/mobileMixin/index.js";

export default {
  name: "ImageDownloadMenu",
  components: {
    GuiLink,
    ObjectCitation
  },
  mixins: [breakpointsMixin, mobileMixin],
  provide() {
    return {
      analyticsListener: (event, url, to) => {
        this.logEvent(to, url);
      }
    };
  },
  props: {
    /**
     * The an array of artists (string)
     */
    artist: {
      type: Array,
      required: true
    },
    /**
     * The caption object for the modal order forms
     */
    captionDetails: {
      type: Object,
      required: true
    },
    /**
     * the indexedId of the object
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Whether the menu is open or not
     */
    isOpen: {
      type: Boolean,
      default: false
    },
    /**
     * The URL for the manifest
     */
    manifestUrl: {
      type: String,
      default: undefined
    },
    /**
     * Whether we're in portrait or landscape orientation
     */
    orientation: {
      type: String,
      required: false,
      default: "Portrait"
    },
    /**
     * The Visual Item JSON-LD object for the selected item
     */
    mediaInfo: {
      type: Object,
      required: true
    },
    /**
     * Whether to show the manifest link
     */
    showManifest: {
      type: Boolean,
      required: true
    }
  },
  emits: ["buttonLabelReady"],
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * the Current Order image View
       */
      currentOrderImageView: "info",
      /**
       * Whether to shwo the dropdown or not
       */
      showDropdown: false,
      /**
       * The translated additional rights statement for out-of-copyright items
       */
      additionalRightsStatement: this.translatePart(
        "image_dropdown.download_info_open_content"
      ),
      /**
       * The Aria Label for the open content link
       */
      ariaOpenContentLink: this.translatePart(
        "image_dropdown.open_content_link"
      ),
      /**
       * the Aria label for the Open Content FAQ link
       */
      ariaOpenContentFaqLink: this.translatePart(
        "image_dropdown.open_content_faq_link"
      ),
      /**
       * the Aria label for the Open Content Info link
       */
      ariaOpenContentInfoLink: this.translatePart(
        "image_dropdown.open_content_info_link"
      ),
      /**
       * Aria IIIF info
       */
      ariaIiif: this.translatePart("image_dropdown.ariaIiif"),
      /**
       * The Alt Text for the IIIF Button
       */
      iiifAltText: this.translatePart("image_dropdown.ariaIiif"),
      /**
       * the Link for the Open Content About
       */
      openContentLink:
        "https://www.getty.edu/legal/terms-of-use/#open-content-program",
      /**
       * The link for the Open Content FAQ
       */
      faqLink: "https://www.getty.edu/about/whatwedo/opencontentfaq.html",
      /**
       * The translated link text for the mirador viewer link
       */
      iiifViewerText: this.translatePart("image_dropdown.iiif_viewer"),
      /**
       * The translated string for the image download menu header
       */
      imageDownloadHeader: this.translatePart("image_dropdown.download_header"),
      /**
       * The translated text for the ObjectCitation header
       */
      citationHeader: this.translate("image_dropdown.caption_header"),
      /**
       * the translated text for open Content section header
       */
      openContentHeader: this.translatePart(
        "image_dropdown.open_content_header"
      ),
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      /**
       * The translated text for the open content section
       */
      openContentText: this.translatePart(
        "image_dropdown.download_info_open_content"
      ),
      /**
       * The translated text for the open content info page link
       */
      openContentLinkText: this.translatePart(
        "image_dropdown.open_content_link_text"
      ),
      /**
       * The translated text for the open content FAQs statement
       */
      openContentFaqText: this.translatePart(
        "image_dropdown.download_info_faqs"
      ),
      /**
       * The translated text for the open content FAQs link
       */
      openContentFaqLink: this.translatePart(
        "image_dropdown.open_content_faq_text"
      ),
      /**
       * The translated text for the publication use header
       */
      publicationUseHeader: this.translatePart(
        "image_dropdown.publication_use_header"
      ),
      /**
       * The translated text for first section of the publication use text
       */
      publicationUsePre: this.translatePart(
        "image_dropdown.publication_use_pre"
      ),
      /**
       * The translated text for the publication use form link
       */
      publicationUseLink: this.translatePart(
        "image_dropdown.publication_use_link_text"
      ),
      /**
       * The translated text for the second section of the publication use text
       */
      publicationUsePost: this.translatePart(
        "image_dropdown.publication_use_post"
      ),
      parentWidth: undefined,
      /**
       * the highest resolution download label
       */
      highestResolutionLabel: this.translatePart(
        "image_dropdown.highest_resolution"
      ),
      /**
       * the large resolution download label
       */
      largeResolutionLabel: this.translatePart(
        "image_dropdown.large_resolution"
      ),
      /**
       * the study (smallest) resolution download label
       */
      studyResolutionLabel: this.translatePart(
        "image_dropdown.study_resolution"
      )
    };
  },
  computed: {
    /**
     * Assembles the data necessary to populate the ObjectCitation
     * @returns {object}
     */
    citationFields() {
      let objectCitationFields = {};
      objectCitationFields[".citation"] = this.captionDetails;
      let citationArtists = [];
      // unknown artists were at one point provided as a string, this may no longer be necessary
      if (this.artist == "unknown") {
        citationArtists = ["unknown"];
      } else {
        this.artist.forEach(function (artist) {
          citationArtists.push(artist);
        });
      }
      objectCitationFields[".citation"]["artists"] = citationArtists;
      return objectCitationFields;
    },
    /**
     * The sizes to provide for the download form (small/medium/large)
     * @returns {array} - an array of info for the different sizes including dimensions
     */
    imageDownloadSizes() {
      if (!this.mediaInfo) return;
      // calculate high/medium/low dimensions
      const highResDimensions = {
        width: this.mediaInfo.width,
        height: this.mediaInfo.height
      };
      const mediumResDimensions = this.calculateNewDimensions(
        highResDimensions,
        3000
      );
      const lowResDimensions = this.calculateNewDimensions(
        highResDimensions,
        1024
      );

      // construct objects with size info
      let highResInfo = {
        isFullSize: true,
        size: "full",
        description: this.highestResolutionLabel,
        ...highResDimensions
      };

      let mediumResInfo = mediumResDimensions
        ? {
            isFullSize: false,
            size: "medium",
            description: this.largeResolutionLabel,
            ...mediumResDimensions
          }
        : undefined;

      let lowResInfo = lowResDimensions
        ? {
            isFullSize: false,
            size: "small",
            description: this.studyResolutionLabel,
            ...lowResDimensions
          }
        : undefined;

      // When calculated, emit the mediumRes dimensions string for the button label
      let buttonInfo = mediumResInfo || highResInfo;
      this.$emit("buttonLabelReady", {
        label: this.getResolutionString(buttonInfo),
        url: this.goToDirectDownload(buttonInfo),
        filename: this.getDownloadFilename(buttonInfo.width)
      });

      let items = [lowResInfo, mediumResInfo, highResInfo].filter((e) => e);
      items.forEach((item) => {
        let resolution = this.getResolutionString(item);
        item.ariaResolution = this.translatePart(
          "image_dropdown.ariaResolution",
          { resolution: resolution }
        );
        item.ariaDownload = this.translatePart(
          "image_dropdown.ariaDownloadLink",
          { resolution: resolution }
        );
        item.href = this.goToDirectDownload(item);
        item.filename = this.getDownloadFilename(item.width);
        item.resolution = resolution;
      });

      return items;
    },
    /**
     * The service URL (without params) to be provided to the image publishing forms
     * @returns {String}
     */
    imageFormServiceUrl() {
      return this.mediaInfo.serviceUrl;
    },
    /**
     * The Mirador Viewer URL
     * @returns {string}
     */
    miradorUrl() {
      return configHelpers.getMiradorUrl(this.manifestUrl);
    }
  },
  watch: {
    /**
     * resize the menu when it's requested to open
     */
    isOpen() {
      this.onResize();
    }
  },
  methods: {
    /**
     * handle the resize of the page
     */
    onResize() {
      this.isMobile = this.isMediumMinus();
      this.$nextTick(() => {
        let width = this.$parent.$parent.$el.offsetWidth;
        if (this.orientation != "Portrait" && !this.isMobile) {
          width = width / 2;
        }

        // if you open the IIIF Viewer in full-screen, the width gets reset to 0, this prevents that
        if (width < 1) {
          return;
        }
        this.parentWidth = `${width}px !important`;
      });
    },
    /**
     * Log a click event
     * @param {to} route
     * @param {string} url
     */
    logEvent(to, url) {
      if (url != undefined) {
        this.logUrlEvent(url);
      }
      if (to != undefined) {
        this.logToEvent(to);
      }
    },
    /**
     * Log a URL click event
     * @param {string} url
     */
    logUrlEvent(url) {
      if (url.includes("media.getty.edu")) {
        let action = undefined;
        let category = undefined;
        let label = undefined;

        if (url.includes("iiifviewer")) {
          action = "mirador iiif comparison viewer";
          category = "Manifest Comparison";
          label = this.manifestUrl;
        } else {
          let size = undefined;
          if (url.indexOf("size=") > 0) {
            size = url.substring(url.indexOf("size=") + 5);
          }
          action = "image download";
          category = `Image Download (${size})`;
          label = this.id;
        }
        this.eventBus.emit("AnalyticsEvent", {
          action: action,
          category: category,
          label: label,
          noninteraction: true
        });
      }
    },
    /**
     * Log a To route click event
     * @param {to} route
     */
    logToEvent(to) {
      if (to.name == "imageRequest") {
        let id = undefined;
        let type = undefined;
        if (to.query) {
          if (to.query.id) {
            id = to.query.id[0];
          }
          if (to.query.type) {
            type = to.query.type;
          }
        }
        this.eventBus.emit("AnalyticsEvent", {
          action: "image request",
          category: `Image Request (${type})`,
          label: id,
          noninteraction: true
        });
      }
    },
    /**
     * Given a set of dimensions and a new long-edge size, return new dimensions of the same ratio
     *
     * @param {object} dimensions - The dimensions whose ratio you want {width: number, height: number}
     * @param {number} newLongEdgeSize - The size you want the long edge of the new dimensions to be
     * @return {object|undefined} The new dimensions with the same ratio as the provided dimensions {width: number, height: number}
     *      or return undefined if the provided dimensions are smaller than new maximum
     *
     */
    calculateNewDimensions(dimensions, newLongEdgeSize) {
      let longEdgeType;
      let longEdgeSize;
      let shortEdgeSize;
      let newDimensions = {};

      // first check if largest size in manifest is larger than
      // our minimum
      if (
        newLongEdgeSize > dimensions.width &&
        newLongEdgeSize > dimensions.height
      ) {
        return;
      }

      // figure out if the width or height is the longer edge
      if (dimensions.width > dimensions.height) {
        longEdgeType = "width";
        longEdgeSize = dimensions.width;
        shortEdgeSize = dimensions.height;
      } else {
        longEdgeType = "height";
        longEdgeSize = dimensions.height;
        shortEdgeSize = dimensions.width;
      }

      // calculate new length of shorter edge
      const newShortEdgeSize = Math.round(
        (shortEdgeSize * newLongEdgeSize) / longEdgeSize
      );

      if (longEdgeType === "width") {
        newDimensions.width = newLongEdgeSize;
        newDimensions.height = newShortEdgeSize;
      } else {
        newDimensions.width = newShortEdgeSize;
        newDimensions.height = newLongEdgeSize;
      }

      return newDimensions;
    },
    /**
     * Generates the filename for a downloadable image to be supplied
     * to the 'download' attribute of an href tag
     */
    getDownloadFilename(width) {
      let uuid = this.mediaInfo.iiifImageId.split("/").pop();
      let filename = `${uuid}_${width}.jpg`;
      return filename;
    },
    /**
     * Gets the resolution dimensions string
     * @param {object} sizeObj - the size object
     *
     * @returns {string} - the dimensions string
     */
    getResolutionString(sizeObj) {
      let resString = sizeObj.description + " (";
      resString += sizeObj.width + " x ";
      resString += sizeObj.height + " px)";
      return resString;
    },
    /**
     * Created the target route to a IIIF image service URL for a given
     * image size for a direct download
     *
     * @param {object} - the size object
     *
     * @returns {string} - formatted the url for the direct download
     */
    goToDirectDownload(sizeObj) {
      let selectedImageUrl = this.mediaInfo.serviceUrl;
      let iiifSize = sizeObj.isFullSize ? "full" : sizeObj.width + ",";
      let filename = this.getDownloadFilename(sizeObj.width);
      // the query parameter above is what will tell GCIS to force the download by setting the content-disposition header
      // and it should also assign the filename for us
      let iiifUrl = `${selectedImageUrl}/full/${iiifSize}/0/default.jpg?download=${filename}&size=${sizeObj.size}`;
      return iiifUrl;
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
